import * as React from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { useAgencyId } from '@/data/rpc';
import { normalizeQuery, toSearchHit, type SearchHit } from '@/lib/search';

/**
 * The global search read (change spec 2026-09-24/topbar, design 1): `portal_search` for the CURRENT
 * agency, debounced while the office types. The server rides RLS, so a hit is exactly a row the
 * caller's own screens would show; nothing is filtered here.
 *
 * A newer query supersedes an older one: an answer that arrives late for a query the box no longer
 * holds is dropped, so the list never shows the hits of what was typed three keys ago.
 */

export const SEARCH_DEBOUNCE_MS = 180;
export const SEARCH_LIMIT = 20;

export type SearchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; query: string; hits: SearchHit[] };

export function useSearch(input: string): SearchState {
  const { client } = useAuth();
  const agencyId = useAgencyId();
  const query = normalizeQuery(input);
  const [state, setState] = React.useState<SearchState>({ status: 'idle' });
  const seq = React.useRef(0);

  React.useEffect(() => {
    const mine = ++seq.current;
    if (!query || !agencyId) {
      setState({ status: 'idle' });
      return;
    }
    setState((prev) => (prev.status === 'ready' ? prev : { status: 'loading' }));
    const timer = setTimeout(() => {
      void (async () => {
        const { data, error } = await client.rpc('portal_search', { p_agency: agencyId, p_q: query, p_limit: SEARCH_LIMIT });
        if (mine !== seq.current) return;
        if (error) {
          console.warn('useSearch: portal_search failed', error);
          setState({ status: 'error' });
          return;
        }
        const hits = ((data ?? []) as Record<string, unknown>[]).map(toSearchHit).filter((h): h is SearchHit => h !== null);
        setState({ status: 'ready', query, hits });
      })();
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [client, agencyId, query]);

  return state;
}
